import type { WorkflowDefinition } from "../types";

/**
 * W9 — Build a pitch list AI can see.
 *
 * `shared`: the pitch list is the property's, not one person's. Two people
 * adding outlets to the same pitch list are doing the same job, so they
 * continue the same checklist rather than forking two.
 *
 * Nothing here bills today. Publisher value is free to score outright;
 * placement quality is metered but priced at zero by product decision. That
 * matters because the whole playbook is about scoring outlets *before*
 * committing outreach hours rather than after the story runs — a price on
 * either would defeat it, and would also oblige a rewrite of this note and of
 * the step copy below.
 */
export const pitchListWorkflow: WorkflowDefinition = {
	id: "pitch-list",
	wNumber: 9,
	title: "Build a PR pitch list AI can actually see",
	outcome:
		"Track the amount of earned media that goes to outlets AI can read and cite.",
	audiences: ["pr"],
	uses: ["Publisher Lookup", "Pitch Lists", "Placements", "Coverage Groups"],
	scope: "property",
	concurrency: "shared",
	progress: "observed",
	// Publisher Lookup scores a domain against the subject being tracked, which
	// a BRAND, a PRODUCT and a PERSON all have. CATEGORY has no subject to
	// pitch for; a LOCATION pitches through its parent.
	isApplicable: (mode) => mode.showPublisherLookup,
	articleSlug: "how-to-build-a-pr-pitch-list-ai-can-actually-see",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "pitch-list",
			title: "Score the outlets before you pitch them",
			page: "Pitch Lists",
			route: "/pitch-lists",
			criterion: "You've added at least one outlet to a pitch list.",
			why: "A media list built on reach and relationships is still a guess about AI. This score says whether AI can read the outlet at all.",
			docsHref: "/docs/methodology/ai-placement-value-score",
			tasks: [
				"Look up every candidate domain and read the tier",
				"Open the breakdown by AI platform before dropping an outlet",
				"Add the outlets worth pitching to a pitch list so it outlasts this campaign",
			],
			complete: { kind: "count", key: "pitchListItemCount", gte: 1 },
			spend: {
				kind: "placementScore",
				count: "fromState",
				note: "Currently free. Scoring outlets and placements costs nothing, so score the whole list.",
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Outlets on a pitch list",
							value: state.property.pitchListItemCount,
							target: 1,
						}
					: null,
		},
		{
			id: "prospective",
			title: "Price the placement before you negotiate",
			page: "Placements",
			route: "/placements/new",
			criterion:
				"You've scored at least one placement you have not landed yet.",
			why: "This prices a placement that does not exist yet. It turns a vague ask into a specific one you can negotiate.",
			docsHref: "/docs/dashboards/placements",
			tasks: [
				"Score the placement you intend to ask for",
				"Score it again under better terms to see what moves most",
				"Take the number into the conversation",
			],
			complete: { kind: "count", key: "placements.prospective", gte: 1 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Placements priced before pitching",
							value: state.property.placements.prospective,
							target: 1,
						}
					: null,
		},
		{
			id: "retrospective",
			title: "Score the placement after it publishes",
			page: "Placements",
			route: "/placements",
			criterion: "You've scored at least one published placement.",
			why: "A negotiated term is a promise, not a placement. Scoring the live page shows the gap between what was agreed and what shipped.",
			docsHref: "/docs/dashboards/placements",
			tasks: [
				"Score the live URL once the story is published",
				"Compare it against the price you negotiated toward",
				"Note the gap for your next pitch to that outlet",
			],
			complete: {
				kind: "count",
				key: "placements.retrospective",
				gte: 1,
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Published placements scored",
							value: state.property.placements.retrospective,
							target: 1,
						}
					: null,
		},
		{
			id: "add-to-group",
			title: "Roll the placements into a coverage group",
			page: "Coverage Groups",
			route: "/coverage-groups",
			criterion:
				"You've added at least one placement to a coverage group.",
			why: "A pitch list changes as outlets are added and stories land. A group answers one question over time, not one at a time.",
			docsHref: "/docs/dashboards/coverage-groups",
			tasks: [
				"Paste the landed URLs into a coverage group",
				"Keep adding as the list delivers, because matches backfill",
				"Attach the group to the PR project",
			],
			complete: {
				kind: "count",
				key: "coverageGroupItemsWithPlacement",
				gte: 1,
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: `${state.property.coverageGroupCount} coverage groups`,
									done: state.property.coverageGroupCount > 0,
								},
								{
									label: `${state.property.coverageGroupItemsWithPlacement} items backed by a scored placement`,
									done:
										state.property
											.coverageGroupItemsWithPlacement >
										0,
								},
							],
						}
					: null,
		},
	],
};
